import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Bot, RefreshCw, Loader2, ShieldAlert, CheckCircle2, Clock,
  AlertTriangle, Zap, Activity, ChevronRight, ArrowLeft, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { isFeatureLive } from '../lib/caraloom_v2_ia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Bot, label: 'Orchestrator', active: true },
];

const TERMINAL_TONE = {
  PLACED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PLACED_SIMULATED: 'bg-amber-50 text-amber-700 border-amber-200',
  ESCALATED: 'bg-red-50 text-red-700 border-red-200',
  DECLINED_ALL: 'bg-red-50 text-red-700 border-red-200',
  AWAITING_ACCEPT: 'bg-sky-50 text-sky-700 border-sky-200',
};

function terminalTone(state) {
  return TERMINAL_TONE[state] || 'bg-slate-100 text-slate-700 border-slate-200';
}

function fmtElapsed(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export default function AdminOrchestrator() {
  const { getAuthHeader } = useAuth();
  const [traces, setTraces] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [runInput, setRunInput] = useState('');
  const [running, setRunning] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const featureOn = isFeatureLive('referrals.orchestrator');

  const loadList = useCallback(async () => {
    if (!featureOn) { setLoading(false); return; }
    setLoading(true);
    try {
      const [rList, rAnalytics] = await Promise.all([
        axios.get(`${API}/orchestrator/traces?limit=50`, getAuthHeader()),
        axios.get(`${API}/orchestrator/analytics`, getAuthHeader()),
      ]);
      setTraces(rList.data?.traces || []);
      setAnalytics(rAnalytics.data || null);
    } catch (e) {
      if (e.response?.status !== 402 && e.response?.status !== 404) {
        toast.error(e.response?.data?.detail || 'Could not load orchestrator traces');
      }
    } finally { setLoading(false); }
  }, [getAuthHeader, featureOn]);

  const loadDetail = useCallback(async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/orchestrator/traces/${id}`, getAuthHeader());
      setDetail(r.data?.trace || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load trace detail');
    }
  }, [getAuthHeader]);

  const runLoop = useCallback(async () => {
    const rid = (runInput || '').trim();
    if (!rid) { toast.error('Enter a referral id'); return; }
    setRunning(true);
    try {
      const r = await axios.post(
        `${API}/orchestrator/referrals/${rid}/run`, {}, getAuthHeader(),
      );
      toast.success(`Loop finished — terminal ${r.data?.trace?.terminal_state || r.data?.trace?.state || 'AWAITING_ACCEPT'}`);
      await loadList();
      if (r.data?.trace?.id) await loadDetail(r.data.trace.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run orchestrator loop');
    } finally { setRunning(false); }
  }, [runInput, getAuthHeader, loadList, loadDetail]);

  const resolveGate = useCallback(async (traceId, gateId, decision) => {
    try {
      const r = await axios.post(
        `${API}/orchestrator/gates/${gateId}/approve`,
        { trace_id: traceId, decision },
        getAuthHeader(),
      );
      setDetail(r.data?.trace || null);
      toast.success(`Gate ${decision}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve gate');
    }
  }, [getAuthHeader]);

  const advanceWave = useCallback(async (traceId) => {
    setAdvancing(true);
    try {
      const r = await axios.post(
        `${API}/orchestrator/traces/${traceId}/advance-wave`,
        { note: 'admin escalated to next wave' },
        getAuthHeader(),
      );
      setDetail(r.data?.trace || null);
      const term = r.data?.trace?.terminal_state;
      if (term === 'DECLINED_ALL') {
        toast.error('No more candidates — trace terminated as DECLINED_ALL');
      } else {
        toast.success(`Advanced to wave ${r.data?.summary?.waves_run || '?'}`);
      }
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not advance wave');
    } finally { setAdvancing(false); }
  }, [getAuthHeader, loadList]);

  useEffect(() => { loadList(); }, [loadList]);

  if (!featureOn) {
    return (
      <DashboardShell
        navItems={ADMIN_NAV}
        title="Autonomous Discharge Orchestrator"
        subtitle="Currently disabled for this environment."
        testId="admin-orchestrator-page"
      >
        <div className="cn-card p-8 text-center" data-testid="orchestrator-flag-off">
          <Bot className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <div className="cn-h3 mb-2">Orchestrator not enabled</div>
          <p className="text-cn-text-muted text-sm">
            Enable the <code className="text-cn-primary">referrals.orchestrator</code> feature
            flag (set <code>REACT_APP_CARALOOM_TIER</code> or flip the entitlement
            for this org) to unlock the autonomous discharge loop.
          </p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Autonomous Discharge Orchestrator"
      subtitle="Closes the full referral → placement → first-visit → 30-day-watch loop. Every mocked integration is surfaced honestly in the trace."
      testId="admin-orchestrator-page"
    >
      {/* Run bar */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="orchestrator-run-bar">
        <div className="flex-1 min-w-[240px]">
          <Input
            value={runInput}
            onChange={(e) => setRunInput(e.target.value)}
            placeholder="Paste a referral id to run the loop…"
            data-testid="orchestrator-referral-input"
            className="!bg-white"
          />
        </div>
        <Button
          onClick={runLoop}
          disabled={running}
          className="cn-btn cn-btn-primary !py-2"
          data-testid="orchestrator-run-btn"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          {running ? 'Running…' : 'Run loop'}
        </Button>
        <Button onClick={loadList} className="cn-btn cn-btn-ghost !py-2" data-testid="orchestrator-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {loading && (
        <div className="cn-card p-10 text-center" data-testid="orchestrator-loading">
          <Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" />
        </div>
      )}

      {!loading && !selectedId && (
        <>
          {analytics && analytics.total_traces > 0 && (
            <AnalyticsPanel data={analytics} />
          )}
          <TraceList traces={traces} onOpen={loadDetail} />
        </>
      )}

      {selectedId && detail && (
        <TraceDetail
          trace={detail}
          onBack={() => { setSelectedId(null); setDetail(null); }}
          onResolveGate={resolveGate}
          onAdvanceWave={() => advanceWave(detail.id)}
          advancing={advancing}
        />
      )}
    </DashboardShell>
  );
}

function AnalyticsPanel({ data }) {
  const total = data.total_traces || 0;
  const active = data.active_traces || 0;
  const placements = data.placements_total || 0;
  const wave1Pct = Math.round((data.wave_1_accept_rate || 0) * 100);
  const rescuePct = Math.round((data.wave_2_plus_rescue_rate || 0) * 100);
  const simulatedPct = Math.round((data.simulated_share || 0) * 100);
  const avgSec = data.avg_time_to_placement_ms
    ? (data.avg_time_to_placement_ms / 1000).toFixed(1)
    : null;
  const topMocked = data.top_mocked_integrations || [];

  return (
    <div className="cn-card p-5 mb-6" data-testid="orchestrator-analytics-panel">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-cn-primary" />
        <h2 className="font-semibold text-cn-text">Orchestrator performance</h2>
        <span className="text-xs text-cn-text-muted">
          · {total} trace{total === 1 ? '' : 's'} · {active} active
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatTile
          label="Wave-1 accept rate"
          value={placements > 0 ? `${wave1Pct}%` : '—'}
          hint={placements > 0 ? `${data.wave_at_accept?.['1'] || 0} of ${placements} placements` : 'no placements yet'}
          tone="teal"
          testid="orch-stat-wave1-rate"
        />
        <StatTile
          label="Rescued by wave 2+"
          value={placements > 0 ? `${rescuePct}%` : '—'}
          hint={placements > 0 ? `${placements - (data.wave_at_accept?.['1'] || 0)} placements` : ''}
          tone={rescuePct > 0 ? 'cyan' : 'slate'}
          testid="orch-stat-rescue-rate"
        />
        <StatTile
          label="Avg waves per placement"
          value={placements > 0 ? (data.avg_waves_when_accepted || 0).toFixed(2) : '—'}
          hint="lower is better"
          tone="slate"
          testid="orch-stat-avg-waves"
        />
        <StatTile
          label="Avg time-to-placement"
          value={avgSec ? `${avgSec}s` : '—'}
          hint={placements > 0 ? 'from loop start to accept' : ''}
          tone="slate"
          testid="orch-stat-avg-ttp"
        />
        <StatTile
          label="Simulated placements"
          value={placements > 0 ? `${simulatedPct}%` : '—'}
          hint={placements > 0 ? `${data.placements_simulated}/${placements} via mocks` : ''}
          tone={simulatedPct >= 50 ? 'amber' : 'emerald'}
          testid="orch-stat-simulated-share"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-cn-text-muted mb-2 font-bold">
            Terminal states
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="orch-stat-terminals">
            {Object.entries(data.terminal_distribution || {}).length === 0 && (
              <span className="text-xs text-cn-text-muted italic">No terminal traces yet</span>
            )}
            {Object.entries(data.terminal_distribution || {}).map(([state, count]) => (
              <span
                key={state}
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${terminalTone(state)}`}
                data-testid={`orch-terminal-${state}`}
              >
                {state} · {count}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-cn-text-muted mb-2 font-bold">
            Top mocked integrations
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="orch-stat-top-mocked">
            {topMocked.length === 0 && (
              <span className="text-xs text-cn-text-muted italic">
                No mocked integrations recorded — the honesty contract has nothing to flag.
              </span>
            )}
            {topMocked.map((m) => (
              <span
                key={m.tool}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                title="Activate the corresponding real integration to remove this dependency."
              >
                <ShieldAlert className="w-3 h-3" /> {m.tool} · {m.count}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const STAT_TONE = {
  teal:    'text-teal-700',
  cyan:    'text-cyan-700',
  emerald: 'text-emerald-700',
  amber:   'text-amber-700',
  slate:   'text-slate-700',
};

function StatTile({ label, value, hint, tone, testid }) {
  return (
    <div
      className="rounded-lg border border-cn-border bg-white p-3"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">
        {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${STAT_TONE[tone] || 'text-cn-text'}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-cn-text-muted mt-0.5 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}


function TraceList({ traces, onOpen }) {
  if (!traces?.length) {
    return (
      <div className="cn-card p-8 text-center" data-testid="orchestrator-empty">
        <Bot className="w-8 h-8 mx-auto text-slate-400 mb-2" />
        <div className="text-cn-text-muted text-sm">
          No orchestrator runs yet. Paste a referral id above to close your first loop.
        </div>
      </div>
    );
  }
  return (
    <div className="cn-card overflow-hidden" data-testid="orchestrator-trace-list">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Referral</th>
            <th className="px-4 py-3">Terminal</th>
            <th className="px-4 py-3">Wave</th>
            <th className="px-4 py-3">LACE</th>
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Mocked</th>
            <th className="px-4 py-3">Gates open</th>
            <th className="px-4 py-3">Elapsed</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {traces.map((t) => {
            const stateLabel = t.terminal_state || t.state;
            const waves = t.waves_run || 0;
            const queueLeft = t.candidate_queue_remaining || 0;
            return (
              <tr
                key={t.id}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => onOpen(t.id)}
                data-testid={`orchestrator-trace-row-${t.id}`}
              >
                <td className="px-4 py-3 font-mono text-xs text-cn-text-muted">{t.referral_id}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${terminalTone(stateLabel)}`}>
                    {stateLabel === 'PLACED_SIMULATED' && <AlertTriangle className="w-3 h-3" />}
                    {stateLabel}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {waves > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 text-cyan-800 text-xs px-1.5 py-0.5 rounded bg-cyan-50 border border-cyan-200"
                      title={`${waves} wave${waves === 1 ? '' : 's'} attempted • ${queueLeft} candidates queued`}
                      data-testid={`orchestrator-wave-badge-${t.id}`}
                    >
                      W{waves}{queueLeft > 0 && <span className="opacity-70">·{queueLeft}</span>}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3">{t.lace_score ?? '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{t.matched_provider_id ? t.matched_provider_id.slice(0, 8) + '…' : '—'}</td>
                <td className="px-4 py-3">
                  {t.integrations_mocked?.length ? (
                    <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                      <ShieldAlert className="w-3 h-3" />
                      {t.integrations_mocked.length}
                    </span>
                  ) : (
                    <span className="text-emerald-700 text-xs">live</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {t.human_gates_open ? (
                    <span className="text-red-600 font-semibold">{t.human_gates_open}</span>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-cn-text-muted text-xs">{fmtElapsed(t.time_to_placement_ms)}</td>
                <td className="px-4 py-3"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TraceDetail({ trace, onBack, onResolveGate, onAdvanceWave, advancing }) {
  const stateLabel = trace.terminal_state || trace.state;
  const openGates = (trace.human_gates_hit || []).filter((g) => !g.resolved_at);
  const closedGates = (trace.human_gates_hit || []).filter((g) => !!g.resolved_at);
  const wavesRun = trace.waves_run || 0;
  const waveHistory = trace.wave_history || [];
  const queueRemaining = (trace.candidate_queue || []).length;
  const canAdvance = !trace.terminal_state && queueRemaining > 0;

  return (
    <div className="space-y-6" data-testid="orchestrator-trace-detail">
      <div className="cn-card p-4 flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-cn-primary text-sm" data-testid="orchestrator-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to traces
        </button>
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs ${terminalTone(stateLabel)}`}>
          {stateLabel}
        </span>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile label="Referral" value={<span className="font-mono text-[0.7rem]">{trace.referral_id?.slice(0, 12)}…</span>} />
        <MiniTile label="LACE" value={trace.lace_score ?? '—'} />
        <MiniTile label="Provider" value={trace.matched_provider_id ? trace.matched_provider_id.slice(0, 8) + '…' : '—'} />
        <MiniTile label="Elapsed" value={fmtElapsed(trace.time_to_placement_ms)} />
      </div>

      {/* Wave escalation panel (Phase 2) */}
      {wavesRun > 0 && (
        <div className="cn-card p-4" data-testid="orchestrator-wave-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-cn-primary" />
              Offer waves — {wavesRun} attempted
              {queueRemaining > 0 && (
                <span className="text-xs font-normal text-cn-text-muted">
                  · {queueRemaining} candidate{queueRemaining === 1 ? '' : 's'} queued
                </span>
              )}
            </div>
            {canAdvance && (
              <Button
                onClick={onAdvanceWave}
                disabled={advancing}
                className="cn-btn cn-btn-ghost !py-1 !text-xs"
                title="Expire the current offer and promote the next-best candidate"
                data-testid="orchestrator-advance-wave-btn"
              >
                {advancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                Advance to next wave
              </Button>
            )}
          </div>
          <ol className="space-y-1.5">
            {waveHistory.map((w) => {
              const outcomeTone = w.outcome === 'accepted'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : w.outcome === 'declined'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : w.outcome === 'expired'
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-sky-50 text-sky-700 border-sky-200';
              return (
                <li
                  key={w.wave}
                  className="flex items-center gap-3 text-sm border border-slate-100 rounded px-3 py-1.5"
                  data-testid={`orchestrator-wave-row-${w.wave}`}
                >
                  <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5">
                    W{w.wave}
                  </span>
                  <span className="font-mono text-xs text-cn-text-muted flex-1 truncate">
                    {w.clinician_id?.slice(0, 12) || 'unknown'}…
                  </span>
                  <span className={`px-2 py-0.5 rounded-full border text-xs ${outcomeTone}`}>
                    {w.outcome || 'active'}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Honesty contract */}
      {(trace.integrations_mocked || []).length > 0 && (
        <div className="cn-card p-4 border-l-4 border-amber-500" data-testid="orchestrator-mocked-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-cn-text mb-1">Mocked integrations in this run</div>
              <div className="flex flex-wrap gap-2">
                {trace.integrations_mocked.map((m) => (
                  <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                    {m}
                  </span>
                ))}
              </div>
              <p className="text-xs text-cn-text-muted mt-2">
                {stateLabel === 'PLACED_SIMULATED'
                  ? 'Terminal was suffixed _SIMULATED because material integrations ran mocked. No real webhook was delivered.'
                  : 'These steps ran against non-live integrations. Trace reflects reality — no real webhook was delivered.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Open human gates */}
      {openGates.length > 0 && (
        <div className="cn-card p-4" data-testid="orchestrator-open-gates">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-600" /> Awaiting human sign-off
          </div>
          <div className="space-y-3">
            {openGates.map((g) => (
              <div key={g.id} className="flex items-center justify-between border border-red-200 bg-red-50/40 rounded px-3 py-2">
                <div className="text-sm">
                  <div className="font-medium">{g.gate}</div>
                  <div className="text-xs text-cn-text-muted">{g.note}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onResolveGate(trace.id, g.id, 'approved')}
                    className="cn-btn cn-btn-primary !py-1 !text-xs"
                    data-testid={`orchestrator-approve-${g.id}`}
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={() => onResolveGate(trace.id, g.id, 'rejected')}
                    className="cn-btn cn-btn-ghost !py-1 !text-xs"
                    data-testid={`orchestrator-reject-${g.id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tool call timeline */}
      <div className="cn-card p-4" data-testid="orchestrator-timeline">
        <div className="font-semibold mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-cn-primary" /> Tool call timeline
        </div>
        <ol className="space-y-2">
          {(trace.tool_calls || []).map((c, i) => (
            <li key={i} className="flex items-center gap-3 text-sm border border-slate-100 rounded px-3 py-2">
              {c.ok
                ? <CheckCircle2 className={`w-4 h-4 ${c.mocked ? 'text-amber-500' : 'text-emerald-600'}`} />
                : <AlertTriangle className="w-4 h-4 text-red-600" />}
              <div className="flex-1">
                <div className="font-mono text-xs">{c.tool}</div>
                {c.error && <div className="text-xs text-red-600 mt-0.5">{c.error}</div>}
                {c.mocked && <div className="text-xs text-amber-700 mt-0.5">mocked · {c.mocked_reason}</div>}
              </div>
              <div className="text-xs text-cn-text-muted">{fmtElapsed(c.elapsed_ms)}</div>
            </li>
          ))}
        </ol>
      </div>

      {/* Closed gates (audit log) */}
      {closedGates.length > 0 && (
        <div className="cn-card p-4" data-testid="orchestrator-closed-gates">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Resolved gates
          </div>
          <div className="space-y-2">
            {closedGates.map((g) => (
              <div key={g.id} className="text-xs text-cn-text-muted border-b border-slate-100 pb-2">
                <span className="font-mono">{g.gate}</span> · {g.decision} · by {g.resolver_id?.slice(0, 12) || 'unknown'}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniTile({ label, value }) {
  return (
    <div className="cn-card p-3">
      <div className="text-[0.65rem] uppercase tracking-wide text-cn-text-muted">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}
